import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Divider,
  Box,
  Card,
  CardContent,
  Avatar,
  Stack,
  Alert,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import LocalShipping from '@mui/icons-material/LocalShipping';
import Payment from '@mui/icons-material/Payment';
import CheckCircle from '@mui/icons-material/CheckCircle';
import ArrowBack from '@mui/icons-material/ArrowBack';
import ArrowForward from '@mui/icons-material/ArrowForward';
import PhoneAndroid from '@mui/icons-material/PhoneAndroid';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useUser, Address } from '../context/UserContext';
import { useOrders } from '../context/OrderContext';
import DataService from '../services/dataService';
import { Order } from '../data/mockOrders';

const steps = ['Shipping Address', 'Payment Method', 'Review Order'];

const Checkout: React.FC = () => {
  const navigate = useNavigate();
  const { cartItems, getCartTotal, clearCart } = useCart();
  const { user } = useAuth();
  const { addresses, personalInfo } = useUser();
  const { addOrder, refreshOrders } = useOrders();

  const [activeStep, setActiveStep] = useState(0);
  const [shippingData, setShippingData] = useState({
    firstName: personalInfo.name?.split(' ')[0] || user?.user_metadata?.name?.split(' ')[0] || '',
    lastName: personalInfo.name?.split(' ').slice(1).join(' ') || user?.user_metadata?.name?.split(' ').slice(1).join(' ') || '',
    address1: '',
    address2: '',
    city: 'Nairobi',
    state: '',
    zip: '',
    country: 'Kenya',
    phone: personalInfo.phone || user?.phone || '',
  });
  const [paymentMethod, setPaymentMethod] = useState('mpesa');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showMpesaPrompt, setShowMpesaPrompt] = useState(false);

  // Pre-fill default address if available and form is empty
  React.useEffect(() => {
    const defaultAddr = addresses.find(a => a.isDefault);
    if (defaultAddr && !shippingData.address1) {
      setShippingData(prev => ({
        ...prev,
        address1: defaultAddr.address,
        // Simple heuristic for city/state if included in address string
        // otherwise default to Nairobi/Kenya
      }));
    }
  }, [addresses]);

  const handleSavedAddressSelect = (addressId: string) => {
    const selected = addresses.find(a => a.id === addressId);
    if (selected) {
      setShippingData(prev => ({
        ...prev,
        address1: selected.address,
      }));
    }
  };

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      handlePlaceOrder();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handlePlaceOrder = async () => {
    setIsProcessing(true);

    if (paymentMethod === 'mpesa') {
      setShowMpesaPrompt(true);
      // Simulate user entering PIN on phone
      await new Promise(resolve => setTimeout(resolve, 3000));
      setShowMpesaPrompt(false);
    } else {
      // Simulate card processing
      await new Promise(resolve => setTimeout(resolve, 2000));
    }

    // Create new order
    const shippingAddressStr = `${shippingData.address1}${shippingData.address2 ? ', ' + shippingData.address2 : ''}, ${shippingData.city}, ${shippingData.state ? shippingData.state + ', ' : ''}${shippingData.zip}, ${shippingData.country}`;
    
    try {
      let createdOrderId = `ORD-${Date.now()}`;

      if (user) {
        // Create in Database
        const dbOrder = await DataService.createOrder({
          userId: user.id,
          items: cartItems.map(item => ({
            productId: item.id.toString(),
            quantity: item.quantity,
            price: item.price,
            name: item.name
          })),
          totalAmount: getCartTotal() + 250, // Including shipping
          shippingAddress: shippingData,
          paymentMethod: paymentMethod === 'mpesa' ? 'M-PESA' : 'Credit Card'
        });
        createdOrderId = dbOrder.id;

        // Refresh orders to get the new one from DB
        await refreshOrders();
      } else {
        // Fallback for guest (local storage only via context)
        const newOrder: Order = {
          id: createdOrderId,
          date: new Date().toISOString(),
          status: 'processing',
          total: getCartTotal() + 250,
          items: cartItems.map(item => ({
            id: item.id.toString(),
            productId: item.id.toString(),
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            image: item.image
          })),
          shippingAddress: shippingAddressStr,
          paymentMethod: paymentMethod === 'mpesa' ? 'M-PESA' : 'Credit Card',
        };
        addOrder(newOrder);
      }

      clearCart();
      setIsProcessing(false);
      navigate('/order-success', { state: { orderId: createdOrderId } });
    } catch (error) {
      console.error('Order creation failed:', error);
      setIsProcessing(false);
      // Show error feedback (could add a snackbar here)
      alert('Failed to place order. Please try again.');
    }
  };

  const handleShippingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShippingData({
      ...shippingData,
      [e.target.name]: e.target.value,
    });
  };

  const renderShippingForm = () => (
    <Grid container spacing={3}>
      {addresses.length > 0 && (
        <Grid item xs={12}>
           <Typography variant="subtitle2" gutterBottom>Saved Addresses</Typography>
           <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
             {addresses.map((addr) => (
               <Button 
                 key={addr.id} 
                 variant="outlined" 
                 size="small"
                 onClick={() => handleSavedAddressSelect(addr.id)}
                 startIcon={<LocalShipping />}
               >
                 {addr.type}: {addr.address.substring(0, 20)}...
               </Button>
             ))}
           </Box>
        </Grid>
      )}
      <Grid item xs={12} sm={6}>
        <TextField
          required
          id="firstName"
          name="firstName"
          label="First name"
          fullWidth
          value={shippingData.firstName}
          onChange={handleShippingChange}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          required
          id="lastName"
          name="lastName"
          label="Last name"
          fullWidth
          value={shippingData.lastName}
          onChange={handleShippingChange}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          required
          id="address1"
          name="address1"
          label="Address line 1"
          fullWidth
          value={shippingData.address1}
          onChange={handleShippingChange}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          id="address2"
          name="address2"
          label="Address line 2"
          fullWidth
          value={shippingData.address2}
          onChange={handleShippingChange}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          required
          id="city"
          name="city"
          label="City"
          fullWidth
          value={shippingData.city}
          onChange={handleShippingChange}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          id="state"
          name="state"
          label="State/Province/Region"
          fullWidth
          value={shippingData.state}
          onChange={handleShippingChange}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          required
          id="zip"
          name="zip"
          label="Zip / Postal code"
          fullWidth
          value={shippingData.zip}
          onChange={handleShippingChange}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          required
          id="country"
          name="country"
          label="Country"
          fullWidth
          value={shippingData.country}
          onChange={handleShippingChange}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          required
          id="phone"
          name="phone"
          label="Phone Number"
          fullWidth
          value={shippingData.phone}
          onChange={handleShippingChange}
        />
      </Grid>
    </Grid>
  );

  const renderPaymentForm = () => (
    <FormControl component="fieldset">
      <FormLabel component="legend">Select Payment Method</FormLabel>
      <RadioGroup
        aria-label="payment-method"
        name="payment-method"
        value={paymentMethod}
        onChange={(e) => setPaymentMethod(e.target.value)}
      >
        <FormControlLabel
          value="mpesa"
          control={<Radio />}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box 
                component="img" 
                src="https://upload.wikimedia.org/wikipedia/commons/thumb/1/15/M-PESA_LOGO-01.svg/1200px-M-PESA_LOGO-01.svg.png" 
                alt="M-Pesa" 
                sx={{ height: 35, width: 'auto', ml: 1 }} 
              />
            </Box>
          }
        />
        <FormControlLabel
          value="card"
          control={<Radio />}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography sx={{ mr: 1 }}>Credit/Debit Card</Typography>
              <Box 
                component="img" 
                src="https://upload.wikimedia.org/wikipedia/commons/thumb/5/5e/Visa_Inc._logo.svg/2560px-Visa_Inc._logo.svg.png" 
                alt="Visa" 
                sx={{ height: 25, width: 'auto', ml: 1 }} 
              />
            </Box>
          }
        />
      </RadioGroup>

      {paymentMethod === 'mpesa' && (
        <Alert severity="info" sx={{ mt: 2 }}>
          You will receive an M-Pesa prompt on your phone ({shippingData.phone || "provided number"}) after placing the order.
        </Alert>
      )}
    </FormControl>
  );

  const renderReviewOrder = () => (
    <Box>
      <Typography variant="h6" gutterBottom>Order Summary</Typography>
      <Stack spacing={2}>
        {cartItems.map((item) => (
          <Box key={item.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar src={item.image} variant="rounded" sx={{ width: 50, height: 50, mr: 2 }} />
              <Box>
                <Typography variant="body1">{item.name}</Typography>
                <Typography variant="caption" color="text.secondary">Qty: {item.quantity}</Typography>
              </Box>
            </Box>
            <Typography variant="body1">KSh {(item.price * item.quantity).toLocaleString()}</Typography>
          </Box>
        ))}
      </Stack>
      <Divider sx={{ my: 2 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography>Subtotal</Typography>
        <Typography>KSh {getCartTotal().toLocaleString()}</Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography>Shipping</Typography>
        <Typography>KSh 250</Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Typography variant="h6">Total</Typography>
        <Typography variant="h6" color="primary">KSh {(getCartTotal() + 250).toLocaleString()}</Typography>
      </Box>

      <Grid container spacing={2} sx={{ mt: 3 }}>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" gutterBottom>Shipping To:</Typography>
          <Typography variant="body2">{shippingData.firstName} {shippingData.lastName}</Typography>
          <Typography variant="body2">{shippingData.address1}</Typography>
          <Typography variant="body2">{shippingData.city}, {shippingData.zip}</Typography>
          <Typography variant="body2">{shippingData.phone}</Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" gutterBottom>Payment Method:</Typography>
          <Typography variant="body2" sx={{ textTransform: 'uppercase' }}>{paymentMethod}</Typography>
        </Grid>
      </Grid>
    </Box>
  );

  if (cartItems.length === 0 && activeStep === 0) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom>Your cart is empty</Typography>
        <Button variant="contained" onClick={() => navigate('/marketplace')}>Go to Marketplace</Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mb: 4, pt: 4 }}>
      <Paper variant="outlined" sx={{ my: { xs: 3, md: 6 }, p: { xs: 2, md: 3 } }}>
        <Typography component="h1" variant="h4" align="center" gutterBottom>
          Checkout
        </Typography>
        <Stepper activeStep={activeStep} sx={{ pt: 3, pb: 5 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <React.Fragment>
          {activeStep === 0 && renderShippingForm()}
          {activeStep === 1 && renderPaymentForm()}
          {activeStep === 2 && renderReviewOrder()}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            {activeStep !== 0 && (
              <Button onClick={handleBack} sx={{ mr: 1 }} startIcon={<ArrowBack />}>
                Back
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={isProcessing}
              endIcon={isProcessing ? <CircularProgress size={20} color="inherit" /> : (activeStep === steps.length - 1 ? <CheckCircle /> : <ArrowForward />)}
            >
              {activeStep === steps.length - 1 ? (isProcessing ? 'Processing...' : 'Place Order') : 'Next'}
            </Button>
          </Box>
        </React.Fragment>
      </Paper>

      {/* M-Pesa Simulation Dialog */}
      <Dialog open={showMpesaPrompt}>
        <DialogTitle sx={{ textAlign: 'center' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress color="success" />
            <Typography variant="h6">Check Your Phone</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pb: 4 }}>
          <PhoneAndroid sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
          <Typography variant="body1" gutterBottom>
            An M-PESA payment request has been sent to
          </Typography>
          <Typography variant="h6" color="primary" gutterBottom>
            {shippingData.phone || "your phone number"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please enter your M-PESA PIN to complete the transaction.
          </Typography>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default Checkout;
